import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
  Job,
  PaginatedUserResponse,
  User,
  UserInput,
  UserRole,
  UserStatus,
} from '../generated/gql-types';
import { userActions } from '../user/user.action';
import { upsertMany, upsertOne } from '../util/zustand.util';
import AsyncStorage from '@react-native-async-storage/async-storage';

const WORKERS_PER_PAGE = 500;

// Guards the background pagination loop: a newer fetch, an unmount, or a
// logout invalidates any Dynamo pages still in flight.
let workersFetchSessionId = 0;
let tenantIdWithWorkersFetchInFlight: string | null = null;
let loadedWorkersTenantId: string | null = null;
let workersNextPageKey: string | null = null;
let workersFullyLoadedForTenantId: string | null = null;

const resetWorkersPagination = () => {
  loadedWorkersTenantId = null;
  workersNextPageKey = null;
  workersFullyLoadedForTenantId = null;
};

export const resetWorkersFetchSession = () => {
  workersFetchSessionId += 1;
  tenantIdWithWorkersFetchInFlight = null;
  resetWorkersPagination();
};

// DynamoDB Query stops at 1 MB, so Limit=500 often returns ~120 items. Keep
// requesting until this chunk has WORKERS_PER_PAGE workers or the index ends.
const accumulateWorkersUntilPageSize = async ({
  tenantId,
  startKey,
  isCurrentSession,
}: {
  tenantId: string;
  startKey?: string;
  isCurrentSession: () => boolean;
}): Promise<PaginatedUserResponse> => {
  const users: User[] = [];
  let exclusiveStartKey: string | undefined = startKey;
  let hasMore = true;

  while (users.length < WORKERS_PER_PAGE && hasMore && isCurrentSession()) {
    const page = await userActions.fetchWorkersListByTenantPaginated(
      tenantId,
      [UserRole.Worker],
      WORKERS_PER_PAGE,
      exclusiveStartKey,
    );

    users.push(...(page.users ?? []));
    hasMore = page.hasMore === true && !!page.lastEvaluatedKey;
    exclusiveStartKey = hasMore
      ? (page.lastEvaluatedKey ?? undefined)
      : undefined;
  }

  return {
    users,
    hasMore,
    lastEvaluatedKey: exclusiveStartKey ?? null,
  };
};

interface WorkerState {
  workers: User[];
  isLoading: boolean;
  isFetchingMoreWorkers: boolean;
  error: string | null;
  fetchWorkersForTenant: (tenantId: string) => Promise<void>;
  cancelWorkersFetchForTenant: () => void;
  fetchWorkersForClient: (clientId: string) => Promise<void>;
  fetchWorkerById: (workerId: string) => Promise<User | null>;
  fetchDetailedWorkerById: (workerId: string) => Promise<User | null>;
  getWorkerById: (workerId: string) => User | null;
  getWorkersByIds: (workerIds: string[]) => User[];
  getWorkersWorkedForClient: (clientId: string, jobs: Job[]) => User[];
  getWorkersForClassification: (classificationId: string) => User[];
  getWorkerMapping: () => Record<string, User>; // Map of workerId -> worker
  updateWorker: (
    id: string,
    details: Partial<UserInput>,
  ) => Promise<User | null>;
  updateWorkerStatus: (
    workerId: string,
    status: UserStatus,
  ) => Promise<User | null>;
  updateWorkerAssignedAdmin: (
    workerId: string,
    assignedAdminId: string,
    changeReason?: string | undefined,
  ) => Promise<User | null>;
}

export const useWorkerStore = create<WorkerState>()(
  persist(
    (set, get) => ({
      //   state
      workers: [],
      isLoading: false,
      isFetchingMoreWorkers: false,
      error: null,

      //   actions
      // Resolves once the first filled chunk has landed so the list can paint,
      // then keeps pulling remaining chunks in the background.
      fetchWorkersForTenant: async (tenantId: string) => {
        if (loadedWorkersTenantId && loadedWorkersTenantId !== tenantId) {
          resetWorkersPagination();
          set({ workers: [] });
        }

        if (workersFullyLoadedForTenantId === tenantId) {
          return;
        }

        if (tenantIdWithWorkersFetchInFlight === tenantId) {
          return;
        }

        workersFetchSessionId += 1;
        const fetchSessionId = workersFetchSessionId;
        tenantIdWithWorkersFetchInFlight = tenantId;
        loadedWorkersTenantId = tenantId;

        const isCurrentSession = () => fetchSessionId === workersFetchSessionId;

        const fetchRemainingChunks = async (startKey: string) => {
          let nextPageKey: string | null = startKey;
          set({ isFetchingMoreWorkers: true });
          try {
            while (nextPageKey && isCurrentSession()) {
              const chunk = await accumulateWorkersUntilPageSize({
                tenantId,
                startKey: nextPageKey,
                isCurrentSession,
              });
              if (!isCurrentSession()) {
                return;
              }
              workersNextPageKey = chunk.hasMore
                ? (chunk.lastEvaluatedKey ?? null)
                : null;
              if (!chunk.hasMore) {
                workersFullyLoadedForTenantId = tenantId;
              }
              set((state) => ({
                workers: upsertMany(state.workers, chunk.users),
              }));
              nextPageKey = workersNextPageKey;
            }
          } catch (error) {
            console.error('Error fetching remaining workers:', error);
          } finally {
            if (isCurrentSession()) {
              set({ isFetchingMoreWorkers: false });
              tenantIdWithWorkersFetchInFlight = null;
            }
          }
        };

        if (get().workers.length > 0 && workersNextPageKey) {
          void fetchRemainingChunks(workersNextPageKey);
          return;
        }

        try {
          set({ isLoading: get().workers.length === 0 });
          const firstChunk = await accumulateWorkersUntilPageSize({
            tenantId,
            isCurrentSession,
          });
          if (!isCurrentSession()) {
            return;
          }
          workersNextPageKey = firstChunk.hasMore
            ? (firstChunk.lastEvaluatedKey ?? null)
            : null;
          if (!firstChunk.hasMore) {
            workersFullyLoadedForTenantId = tenantId;
          }
          set((state) => ({
            workers:
              state.workers.length > 0
                ? upsertMany(state.workers, firstChunk.users)
                : (firstChunk.users ?? []),
          }));

          if (workersNextPageKey) {
            void fetchRemainingChunks(workersNextPageKey);
            return;
          }
          tenantIdWithWorkersFetchInFlight = null;
        } catch (error) {
          console.error('Error fetching workers:', error);
          if (isCurrentSession()) {
            tenantIdWithWorkersFetchInFlight = null;
          }
          throw error;
        } finally {
          if (isCurrentSession()) {
            set({ isLoading: false });
          }
        }
      },
      cancelWorkersFetchForTenant: () => {
        workersFetchSessionId += 1;
        tenantIdWithWorkersFetchInFlight = null;
        set({ isLoading: false, isFetchingMoreWorkers: false });
      },
      fetchWorkersForClient: async (clientId: string) => {
        // Cancel any tenant wide background pagination so its pages cannot be
        // merged into this narrower client scoped list.
        workersFetchSessionId += 1;
        tenantIdWithWorkersFetchInFlight = null;
        resetWorkersPagination();
        try {
          set({ isLoading: true, isFetchingMoreWorkers: false });
          const workers = await userActions.fetchUsersWorkedForClient(clientId);
          if (workers) {
            set({ workers });
          } else {
            set({ workers: [] });
          }
        } catch (error) {
          console.error('Error fetching workers for client:', error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },
      fetchDetailedWorkerById: async (workerId: string) => {
        try {
          set({ isLoading: true });
          const worker = await userActions.fetchUserDetailedById(workerId);

          if (worker) {
            set({
              workers: worker ? upsertOne(get().workers, worker) : [],
            });
          }

          if (!worker) {
            set({ error: 'Worker not found' });
          }

          return worker;
        } catch (error) {
          console.error('Error fetching worker:', error);
          return null;
        } finally {
          set({ isLoading: false });
        }
      },
      fetchWorkerById: async (workerId: string) => {
        try {
          set({ isLoading: true });
          const worker = await userActions.fetchUserById(workerId);

          if (worker) {
            set({
              workers: worker ? upsertOne(get().workers, worker) : [],
            });
          }
          if (!worker) {
            set({ error: 'Worker not found' });
          }
          return worker;
        } catch (error) {
          console.error('Error fetching worker:', error);
          return null;
        } finally {
          set({ isLoading: false });
        }
      },

      //   getters
      getWorkerById: (workerId: string) => {
        return (
          get().workers?.find((worker: User) => worker?.id === workerId) || null
        );
      },
      getWorkersByIds: (workerIds: string[]) => {
        const workerIdSet = new Set(workerIds);
        return get().workers?.filter((worker: User) =>
          workerIdSet.has(worker?.id),
        );
      },
      getWorkersWorkedForClient: (clientId: string, jobs: Job[]) => {
        const workerIds = new Set<string>();
        const clientJobs = jobs.filter(
          (job: Job) => job?.clientId === clientId,
        );
        clientJobs.forEach((job: Job) => {
          if (job.userId && !workerIds.has(job.userId)) {
            workerIds.add(job.userId);
          }
        });
        return get().workers?.filter((worker: User) =>
          workerIds.has(worker?.id),
        );
      },
      getWorkersForClassification: (classificationId: string) => {
        return get().workers?.filter((worker: User) =>
          worker?.classificationLevels?.some(
            (classification) =>
              classification?.classificationId === classificationId,
          ),
        );
      },
      getWorkerMapping: () => {
        return (
          get().workers?.reduce(
            (acc, worker) => {
              acc[worker.id] = worker;
              return acc;
            },
            {} as Record<string, User>,
          ) ?? {}
        );
      },
      updateWorker: async (id: string, details: Partial<UserInput>) => {
        try {
          set({ isLoading: true });

          const updatedWorker = await userActions.updateUser(
            id,
            details as UserInput,
          );

          if (updatedWorker) {
            set({ workers: upsertOne(get().workers, updatedWorker) });
          }

          set({ isLoading: false });

          return updatedWorker;
        } catch (error) {
          console.error('Error updating worker:', error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },
      updateWorkerStatus: async (workerId: string, status: UserStatus) => {
        try {
          set({ isLoading: true });
          const updatedUser = await userActions.updateUserStatus(
            workerId,
            status,
          );
          if (updatedUser) {
            set({ workers: upsertOne(get().workers, updatedUser) });
          }
          return updatedUser;
        } catch (error) {
          console.error('Error updating user status:', error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },
      updateWorkerAssignedAdmin: async (
        workerId: string,
        assignedAdminId: string,
        changeReason?: string | undefined,
      ) => {
        try {
          set({ isLoading: true });
          const updatedUser = await userActions.updateUserAssignedAdmin(
            workerId,
            assignedAdminId,
            changeReason,
          );
          if (updatedUser) {
            set({ workers: upsertOne(get().workers, updatedUser) });
          }
          return updatedUser;
        } catch (error) {
          console.error('Error updating assigned admin:', error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },
    }),
    {
      name: 'worker-storage', // This is the key used in localStorage
      storage: createJSONStorage(() => AsyncStorage),
      // Discards the worker list a previous build persisted here, which
      // filled the browser storage quota on large tenants.
      version: 1,
      // Nothing is persisted. A tenant's worker list is far too large for
      // the browser storage quota, and presigned profile picture URLs alone
      // run to a couple of KB each.
      partialize: () => ({}),
    },
  ),
);
